import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "./api";

const BrandingContext = createContext(null);

const DEFAULTS = {
  firm_name: "Barnard Inc",
  tagline: "Lead Tracker",
  primary_color: "#0F172A",
  accent_color: "#002FA7",
  sidebar_bg_color: "#FFFFFF",
  sidebar_text_color: "#0F172A",
  header_bg_color: "#FFFFFF",
  header_text_color: "#0F172A",
  font_display: "Cormorant Garamond",
  font_body: "IBM Plex Sans",
  logo_url: null,
  login_image_url: null,
};

// Google-fonts safe families (loaded via CSS link) + system fonts. The
// "System: …" group lists fonts pre-installed on Windows / macOS / iOS /
// Android so they render without any external download — useful when the
// firm prefers a familiar, native-looking face.
const SYSTEM_FONT_OPTIONS = [
  "System: Arial", "System: Helvetica", "System: Calibri", "System: Cambria",
  "System: Georgia", "System: Tahoma", "System: Times New Roman",
  "System: Trebuchet MS", "System: Verdana", "System: Courier New",
  "System: Consolas",
];

export const FONT_OPTIONS = {
  display: [
    // Classic serif
    "Cormorant Garamond", "Playfair Display", "Cinzel", "DM Serif Display",
    "Lora", "Crimson Pro", "Libre Baskerville", "Bodoni Moda",
    "EB Garamond", "Spectral", "Fraunces", "Lustria", "Cardo",
    // Modern sans-display
    "Inter", "Manrope", "Space Grotesk", "Outfit", "Bebas Neue",
    "Archivo", "Syne", "Big Shoulders Display", "Unbounded", "Sora",
    // System (stacks declared in SYSTEM_FONTS below)
    "System Sans", "System Serif",
    ...SYSTEM_FONT_OPTIONS,
  ],
  body: [
    "IBM Plex Sans", "Inter", "Manrope", "Outfit", "Space Grotesk",
    "DM Sans", "Plus Jakarta Sans", "Lato", "Open Sans", "Source Sans 3",
    "Work Sans", "Nunito Sans", "Roboto", "Mulish", "Karla", "Public Sans",
    // Serif body options
    "Lora", "EB Garamond", "Spectral", "Source Serif 4",
    // System (stacks declared in SYSTEM_FONTS below)
    "System Sans", "System Serif",
    ...SYSTEM_FONT_OPTIONS,
  ],
};

const SYSTEM_FONTS = {
  "System Sans": "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  "System Serif": "Georgia, 'Times New Roman', Times, serif",
  "System: Arial": "Arial, sans-serif",
  "System: Helvetica": "Helvetica, Arial, sans-serif",
  "System: Calibri": "Calibri, 'Segoe UI', sans-serif",
  "System: Cambria": "Cambria, Georgia, serif",
  "System: Georgia": "Georgia, serif",
  "System: Tahoma": "Tahoma, Geneva, sans-serif",
  "System: Times New Roman": "'Times New Roman', Times, serif",
  "System: Trebuchet MS": "'Trebuchet MS', sans-serif",
  "System: Verdana": "Verdana, Geneva, sans-serif",
  "System: Courier New": "'Courier New', Courier, monospace",
  "System: Consolas": "Consolas, 'Lucida Console', monospace",
};

function resolveFontStack(name, fallback) {
  if (!name) return fallback;
  if (SYSTEM_FONTS[name]) return SYSTEM_FONTS[name];
  return `'${name}', ${fallback}`;
}

function ensureGoogleFontLoaded(name) {
  if (!name || SYSTEM_FONTS[name]) return;
  const id = `gf-${name.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function applyBranding(b, themeOverride) {
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", b.primary_color);
  root.style.setProperty("--brand-accent", b.accent_color);
  root.style.setProperty("--brand-sidebar-bg", b.sidebar_bg_color);
  root.style.setProperty("--brand-sidebar-text", b.sidebar_text_color);
  root.style.setProperty("--brand-header-bg", b.header_bg_color);
  root.style.setProperty("--brand-header-text", b.header_text_color);

  const displayStack = resolveFontStack(b.font_display, "Georgia, serif");
  const bodyStack = resolveFontStack(b.font_body, "system-ui, sans-serif");
  root.style.setProperty("--brand-font-display", displayStack);
  root.style.setProperty("--brand-font-body", bodyStack);
  ensureGoogleFontLoaded(b.font_display);
  ensureGoogleFontLoaded(b.font_body);

  const mode = themeOverride || localStorage.getItem("bi_theme") || "system";
  const wantDark = mode === "dark" || (mode === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  root.classList.toggle("dark", wantDark);

  // Browser tab — title and favicon. The default in index.html is the static
  // brand string; runtime swaps reflect whatever the admin configures in
  // Settings → Branding.
  if (b.firm_name) {
    const tab = b.tagline ? `${b.firm_name} | ${b.tagline}` : b.firm_name;
    if (document.title !== tab) document.title = tab;
  }
  if (b.logo_url) {
    // Swap the existing icon link (or insert one). Data URLs work as favicons
    // in modern browsers and survive page reloads from the localStorage cache.
    let link = document.getElementById("bi-favicon");
    if (!link) {
      link = document.createElement("link");
      link.id = "bi-favicon";
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== b.logo_url) {
      link.setAttribute("href", b.logo_url);
      // Best-guess content type so Safari is happy with non-PNG uploads.
      const m = /^data:([^;]+);/.exec(b.logo_url);
      if (m) link.setAttribute("type", m[1]);
    }

    // Apple home-screen icon (iOS / iPadOS) — set it to the brand logo too
    // so installed-as-app users see the firm's logo on their springboard.
    let apple = document.getElementById("bi-apple-icon");
    if (!apple) {
      apple = document.createElement("link");
      apple.id = "bi-apple-icon";
      apple.rel = "apple-touch-icon";
      document.head.appendChild(apple);
    }
    if (apple.getAttribute("href") !== b.logo_url) {
      apple.setAttribute("href", b.logo_url);
    }

    // PWA manifest — point at the backend's dynamic manifest endpoint so the
    // Android / Chrome / Edge "Install app" icon also uses the brand logo.
    // We always have an API base URL available at `process.env.REACT_APP_BACKEND_URL`.
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const manifestHref = `${apiBase}/api/branding/manifest.json`;
    let manifestLink = document.getElementById("bi-manifest");
    if (!manifestLink) {
      manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) manifestLink.id = "bi-manifest";
    }
    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.id = "bi-manifest";
      manifestLink.rel = "manifest";
      document.head.appendChild(manifestLink);
    }
    if (manifestLink.getAttribute("href") !== manifestHref) {
      manifestLink.setAttribute("href", manifestHref);
    }
  }
}

export const BrandingProvider = ({ children }) => {
  // Hydrate from localStorage on first paint so the saved logo / login image
  // is rendered immediately on subsequent visits — no flash of the default
  // placeholder while we wait for /api/branding to respond.
  const initial = (() => {
    try {
      const cached = JSON.parse(localStorage.getItem("bi_branding") || "null");
      if (cached && typeof cached === "object") return { ...DEFAULTS, ...cached };
    } catch (e) {
      console.debug("branding cache hydrate failed:", e?.message);
    }
    return DEFAULTS;
  })();
  const [branding, setBranding] = useState(initial);
  const [loaded, setLoaded] = useState(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem("bi_theme") || "system");

  // Apply hydrated CSS vars immediately so the first render uses cached fonts/colors too.
  useEffect(() => {
    applyBranding(initial, localStorage.getItem("bi_theme") || "system");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branding`);
      const merged = { ...DEFAULTS, ...data };
      setBranding(merged);
      applyBranding(merged, theme);
      try {
        // Persist for instant render on the next page load.
        localStorage.setItem("bi_branding", JSON.stringify(merged));
      } catch (e) {
        console.debug("branding cache persist failed:", e?.message);
      }
    } catch (e) {
      console.debug("branding fetch failed:", e?.response?.status || e?.message);
      applyBranding(DEFAULTS, theme);
    } finally {
      setLoaded(true);
    }
  }, [theme]);

  useEffect(() => { refresh(); }, [refresh]);

  // React to system preference changes
  useEffect(() => {
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyBranding(branding, theme);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, [branding, theme]);

  const update = useCallback((partial) => {
    const merged = { ...branding, ...partial };
    setBranding(merged);
    applyBranding(merged, theme);
  }, [branding, theme]);

  const setTheme = useCallback((next) => {
    localStorage.setItem("bi_theme", next);
    setThemeState(next);
    applyBranding(branding, next);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, loaded, refresh, update, theme, setTheme }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
